// Copyright (c) 2025 TrustCloud Corporation - All rights reserved.
//
// This software contains valuable confidential and proprietary information of
// TrustCloud Corporation
//
// Unauthorized use, reproduction, transmission, or distribution of this file and its
// contents is a violation of applicable law.

import React, { useCallback, useMemo, useState } from 'react';
import { IconButton, TabList, TabRoot, Toast, Typography } from '@kintent/glide';
import { format } from 'date-fns';
import { UilKeySkeleton as KeyIcon, UilExternalLinkAlt as LinkIcon, UilImport } from '@iconscout/react-unicons';
import { Link, useCurrentRoute, useNavigation } from 'react-navi';

import * as Styles from './DocumentList.styles';
import { Flex } from '../../../../../components/Flex';
import { Helmet } from '../../../../../components/Helmet';
import { useAuthService, useDocumentDownload, useFeatureFlag, useTeamDocumentList } from '../../../../../lib/state';
import { downloadFileFromAPI, transformDocuments } from '../../../../../lib/utils';
import {
  DOCUMENT_ACCESS_LEVEL,
  DOCUMENT_TYPE,
  PAGE_ROUTES,
  RESOURCE_TYPE,
  SERVICE_NOW_LOGIN_URL,
} from '../../../../../lib/constants';
import { FEATURE_FLAG } from '../../../../../lib/featureFlags';
import { api } from '../../../../../lib/api';

const getRedirectUrl = (original, isLoggedIn, isEnterpriseGradeTrustShareEnabled) => {
  if (original.accessLevel === DOCUMENT_ACCESS_LEVEL.PRIVATE && isEnterpriseGradeTrustShareEnabled) {
    return SERVICE_NOW_LOGIN_URL;
  }
  if (original.accessLevel === DOCUMENT_ACCESS_LEVEL.PRIVATE && !isLoggedIn) {
    return PAGE_ROUTES.REQUEST_ACCESS;
  }

  if (original.type === DOCUMENT_TYPE.RESOURCE && original.resourceType === RESOURCE_TYPE.LINK) {
    return original.identifier;
  }

  if (original.type === DOCUMENT_TYPE.POLICY) {
    return original.identifier;
  }

  return null;
};

const showActionIcon = (original, isLoggedIn) => {
  if (original.accessLevel === DOCUMENT_ACCESS_LEVEL.PRIVATE && !isLoggedIn) {
    return <KeyIcon size="20px" />;
  }

  if (original.resourceType === RESOURCE_TYPE.LINK) {
    return <LinkIcon size="20px" />;
  }

  if (original.resourceType === RESOURCE_TYPE.FILE) {
    return <UilImport size="20px" />;
  }

  return null;
};

const COLUMNS = [
  {
    accessorFn: (data) => data.title,
    id: 'name',
    header: 'Document Name',
    cell: (props) => {
      const { original } = props.row;
      if (original.accessLevel === DOCUMENT_ACCESS_LEVEL.PRIVATE) {
        return (
          <Flex
            gap="8px"
            alignItems="center"
          >
            <Styles.StyledLockIcon size="16px" />
            <Typography
              level="8"
              strength="60%"
            >
              {original.title}
            </Typography>
          </Flex>
        );
      }
      return <Typography level="8">{original.title}</Typography>;
    },
    sortingFn: 'text',
  },
  {
    accessorFn: (data) => data.description,
    id: 'description',
    header: 'Description',
    cell: (props) => {
      const { original } = props.row;
      return (
        <Typography
          level="8"
          strength={original.accessLevel === DOCUMENT_ACCESS_LEVEL.PRIVATE ? '60%' : '100%'}
        >
          {original.description || '-'}
        </Typography>
      );
    },
    sortingFn: 'text',
  },
  {
    accessorFn: (data) => data.lastModifiedDate,
    id: 'lastUpdated',
    header: 'Last Updated',
    cell: (props) => {
      const lastUpdated = props.getValue();
      return format(new Date(lastUpdated), 'MMM dd, yyyy');
    },
    sortingFn: (a, b) => {
      const aDateToLocaleString = a.original.lastModifiedDate ? new Date(a.original.lastModifiedDate) : null;
      const bDateToLocaleString = b.original.lastModifiedDate ? new Date(b.original.lastModifiedDate) : null;

      return aDateToLocaleString - bDateToLocaleString;
    },
  },
  {
    accessorFn: (data) => data,
    id: 'view',
    header: 'Actions',
    cell: ({ table, row }) => {
      const { original } = row;
      const { isLoggedIn, teamId, setShowErrorToast } = table.options.meta;

      const downloadResource = useDocumentDownload(teamId, original.id);

      const handleDocumentDownload = async () => {
        try {
          await downloadFileFromAPI(() => downloadResource(), original.identifier);
        } catch (e) {
          setShowErrorToast(true);
        }
      };

      const handleIconClick = (e) => {
        e.stopPropagation();

        if (original.resourceType === RESOURCE_TYPE.FILE) {
          handleDocumentDownload();
        }
      };

      const isEnterpriseGradeTrustShareEnabled = useFeatureFlag(FEATURE_FLAG.ENTERPRISE_GRADE_TS);
      const redirectUrl = getRedirectUrl(original, isLoggedIn, isEnterpriseGradeTrustShareEnabled);
      const icon = showActionIcon(original, isLoggedIn);

      return (
        <IconButton
          aria-label="Go to document"
          as={Link}
          onClick={handleIconClick}
          data-testid={`document-link-${original.id}`}
          href={redirectUrl}
          icon={icon}
          target="_blank"
        />
      );
    },
  },
];

const VIEW_TYPES = {
  ALL: 'all',
  PUBLIC: 'public',
  PRIVATE: 'private',
};

function DocumentList() {
  const { currentTeam, authenticatedUser } = useAuthService();
  const {
    url: { query },
  } = useCurrentRoute();

  const [documents] = useTeamDocumentList(currentTeam?.id);
  const { navigate } = useNavigation();

  const [view, setView] = useState(VIEW_TYPES.ALL);
  const [showErrorToast, setShowErrorToast] = useState(false);

  const filteredDocuments = useMemo(() => {
    if (view !== VIEW_TYPES.ALL) {
      return documents.filter((document) => document.accessLevel === view);
    }
    return documents;
  }, [documents, view]);

  const isEnterpriseGradeTrustShareEnabled = useFeatureFlag(FEATURE_FLAG.ENTERPRISE_GRADE_TS);

  const allArtifacts = useMemo(() => {
    return transformDocuments(
      filteredDocuments.filter((document) => {
        if (query.section) {
          return document.sections?.some((section) => section.name === query.section);
        }
        return true;
      })
    );
  }, [filteredDocuments, query.section]);

  const handleViewChange = (newView) => {
    setView(newView);
  };

  const onRowClick = useCallback(
    async (row) => {
      if (row.accessLevel === DOCUMENT_ACCESS_LEVEL.PRIVATE && isEnterpriseGradeTrustShareEnabled === false) {
        navigate(PAGE_ROUTES.REQUEST_ACCESS);
        return;
      }
      if (row.accessLevel === DOCUMENT_ACCESS_LEVEL.PRIVATE && isEnterpriseGradeTrustShareEnabled) {
        window.open(SERVICE_NOW_LOGIN_URL, '_blank');
        return;
      }

      if (row.type === DOCUMENT_TYPE.RESOURCE && row.resourceType === RESOURCE_TYPE.LINK) {
        window.open(row.identifier, '_blank');
        return;
      }

      /**
       * We're invoking the API directly here because invoking useDocumentDownload()
       * within conditionals violates the rules of React hooks.
       */
      if (row.type === DOCUMENT_TYPE.RESOURCE && row.resourceType === RESOURCE_TYPE.FILE) {
        const downloadResource = () => api.documents.download(currentTeam?.id, row.id);
        try {
          await downloadFileFromAPI(downloadResource, row.identifier);
        } catch (e) {
          setShowErrorToast(true);
        }
      }
    },
    [navigate, currentTeam, isEnterpriseGradeTrustShareEnabled]
  );

  return (
    <>
      <Helmet pageTitle="Documents" />
      <Styles.StyledTable
        className={isEnterpriseGradeTrustShareEnabled ? 'enterprise-document-list' : 'document-list'}
        title={
          <Flex gap="8px">
            <Styles.CounterWrapper>{allArtifacts.length}</Styles.CounterWrapper>
            <Typography level="7">{query.section ? `Section: ${query.section}` : 'All Documents'}</Typography>
          </Flex>
        }
        columns={COLUMNS}
        data={allArtifacts}
        rowAction={onRowClick}
        headerActions={
          <TabRoot
            value={view}
            onValueChange={handleViewChange}
          >
            <TabList>
              <Styles.StyledTabTrigger value={VIEW_TYPES.ALL}>
                <Typography level="8">All</Typography>
              </Styles.StyledTabTrigger>
              <Styles.StyledTabTrigger value={VIEW_TYPES.PUBLIC}>
                <Typography level="8">Public</Typography>
              </Styles.StyledTabTrigger>
              <Styles.StyledTabTrigger value={VIEW_TYPES.PRIVATE}>
                <Typography level="8">Private</Typography>
              </Styles.StyledTabTrigger>
            </TabList>
          </TabRoot>
        }
        columnSelection
        meta={{
          isLoggedIn: authenticatedUser,
          teamId: currentTeam?.id,
          setShowErrorToast,
        }}
        search
        sorting
        subgrid
      />
      <Toast
        variant="negative"
        label="Error"
        description="We were unable to download the requested file. Please contact support or try again."
        open={showErrorToast}
        duration={1000}
        onOpenChange={() => setShowErrorToast(false)}
      />
    </>
  );
}

export default DocumentList;
